import { Loader2, Trash2, X } from "lucide-react";
import BaseDrawer from "@/shared/components/Drawer";
import { Button } from "@/shared/ui/Button";
import { Input } from "@/shared/ui/Input";
import {
  formatCurrencyField,
  normalizeCurrencyTyping,
  RULE_AMOUNT_OPERATOR_OPTIONS,
  RULE_TEXT_OPERATOR_OPTIONS,
} from "./utils";
import type {
  LocalCategorizationRule, LocalRuleCondition, RuleAmountOperator, RuleConditionKind, RuleTextOperator,
} from "./utils";
import type { BankMovementAccount, LedgerAllocationOperationType } from "../../types/accounting.types";

type RulesManagerDrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  // Rule form
  ruleName: string
  setRuleName: (value: string) => void
  ruleConditions: LocalRuleCondition[]
  updateRuleCondition: (conditionId: string, patch: Partial<LocalRuleCondition>) => void
  addRuleCondition: () => void
  removeRuleCondition: (conditionId: string) => void
  ruleBudgetType: "ASC" | "AEP"
  setRuleBudgetType: (value: "ASC" | "AEP") => void
  ruleOperationTypeId: string
  setRuleOperationTypeId: (value: string) => void
  ascOperationTypes: LedgerAllocationOperationType[]
  aepOperationTypes: LedgerAllocationOperationType[]
  bankAccounts: BankMovementAccount[]
  isBankAccountsLoading: boolean
  // Actions
  ruleActionInProgress: string | null
  onCreateRule: () => void
  onToggleRuleActive: (ruleId: string) => void
  onDeleteRule: (ruleId: string) => void
  // Rules list
  sortedLocalRules: LocalCategorizationRule[]
  isCategorizationRulesLoading: boolean
};

const RulesManagerDrawer = ({
  open,
  onOpenChange,
  ruleName,
  setRuleName,
  ruleConditions,
  updateRuleCondition,
  addRuleCondition,
  removeRuleCondition,
  ruleBudgetType,
  setRuleBudgetType,
  ruleOperationTypeId,
  setRuleOperationTypeId,
  ascOperationTypes,
  aepOperationTypes,
  bankAccounts,
  isBankAccountsLoading,
  ruleActionInProgress,
  onCreateRule,
  onToggleRuleActive,
  onDeleteRule,
  sortedLocalRules,
  isCategorizationRulesLoading,
}: RulesManagerDrawerProps) => {
  return (
    <BaseDrawer
      drawerId="DRW-RULES"
      open={open}
      onOpenChange={onOpenChange}
      isFirstLevel={false}
      onBack={() => onOpenChange(false)}
      width="893px"
      zIndex={72}
      bodyClassName="[&_*]:select-text"
      title="Créer une règle de catégorisation"
      description="Gestionnaire dédié compta: associez un mot-clé à une catégorie."
    >
      <div className="space-y-3">
        <div className="space-y-1">
          <label className="text-[11px] text-slate-600">Nom de la règle</label>
          <Input
            value={ruleName}
            onChange={(event) => setRuleName(event.target.value)}
            placeholder="Ex: Frais bancaires CB"
            className="h-8 text-xs"
          />
        </div>

        <div className="rounded-md border bg-white p-2 space-y-2">
          <div className="text-xs font-semibold text-slate-700">Si</div>
          <div className="space-y-1.5">
            {ruleConditions.map((condition, index) => (
              <div key={condition.id} className="space-y-1">
                {index > 0 && (
                  <div className="text-xs font-semibold text-slate-700">Et</div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-[130px_180px_1fr_32px] gap-1.5 items-center">
                  <select
                    className="h-8 rounded border border-slate-200 bg-slate-50 px-2 text-xs font-medium text-slate-700"
                    value={condition.kind}
                    onChange={(event) => updateRuleCondition(condition.id, { kind: event.target.value as RuleConditionKind })}
                  >
                    <option value="keyword">Le mot-clé</option>
                    <option value="amount">Montant</option>
                    <option value="bank_account">Compte bancaire</option>
                  </select>
                  {condition.kind === "keyword"
                    ? (
                      <>
                        <select
                          className="h-8 rounded border border-slate-200 bg-white px-2 text-xs"
                          value={condition.textOperator}
                          onChange={(event) => updateRuleCondition(condition.id, { textOperator: event.target.value as RuleTextOperator })}
                        >
                          {RULE_TEXT_OPERATOR_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <Input
                          value={condition.textValue}
                          onChange={(event) => updateRuleCondition(condition.id, { textValue: event.target.value })}
                          placeholder="Ex: crédit du nord"
                          className="h-8 text-xs"
                        />
                      </>
                    )
                    : condition.kind === "amount"
                      ? (
                        <>
                          <select
                            className="h-8 rounded border border-slate-200 bg-white px-2 text-xs"
                            value={condition.amountOperator}
                            onChange={(event) => updateRuleCondition(condition.id, { amountOperator: event.target.value as RuleAmountOperator })}
                          >
                            {RULE_AMOUNT_OPERATOR_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <div className={condition.amountOperator === "between" ? "grid grid-cols-2 gap-1.5" : ""}>
                            <Input
                              inputMode="decimal"
                              value={condition.amountValue}
                              onChange={(event) => updateRuleCondition(condition.id, { amountValue: normalizeCurrencyTyping(event.target.value) })}
                              onBlur={(event) => updateRuleCondition(condition.id, { amountValue: formatCurrencyField(event.target.value) })}
                              placeholder="0,00"
                              className="h-8 text-xs text-right tabular-nums"
                            />
                            {condition.amountOperator === "between" && (
                              <Input
                                inputMode="decimal"
                                value={condition.amountValueTo}
                                onChange={(event) => updateRuleCondition(condition.id, { amountValueTo: normalizeCurrencyTyping(event.target.value) })}
                                onBlur={(event) => updateRuleCondition(condition.id, { amountValueTo: formatCurrencyField(event.target.value) })}
                                placeholder="0,00"
                                className="h-8 text-xs text-right tabular-nums"
                              />
                            )}
                          </div>
                        </>
                      )
                      : (
                        <>
                          <div className="h-8 rounded border bg-slate-50 px-2 text-xs text-slate-700 flex items-center">
                            Est
                          </div>
                          <select
                            className="h-8 rounded border border-slate-200 bg-white px-2 text-xs"
                            value={condition.bankAccountId}
                            onChange={(event) => updateRuleCondition(condition.id, { bankAccountId: event.target.value })}
                          >
                            <option value="">
                              {isBankAccountsLoading ? "Chargement..." : "Choisir..."}
                            </option>
                            {bankAccounts.map((account) => (
                              <option key={account.id} value={account.id}>
                                {account.name}
                              </option>
                            ))}
                          </select>
                        </>
                      )}
                  <Button
                    type="button"
                    variant="destructiveGhost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => removeRuleCondition(condition.id)}
                    disabled={ruleConditions.length <= 1}
                    aria-label="Supprimer la condition"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {condition.kind === "amount" && (
                  <label className="inline-flex items-center gap-1.5 text-xs text-slate-700">
                    <input
                      type="checkbox"
                      checked={condition.absoluteValue}
                      onChange={(event) => updateRuleCondition(condition.id, { absoluteValue: event.target.checked })}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                    Valeur absolue
                  </label>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs w-full justify-start text-slate-500"
              onClick={addRuleCondition}
            >
              Ajouter une autre condition
            </Button>
          </div>
        </div>

        <div className="rounded-md border bg-white p-2 space-y-2">
          <div className="text-xs font-semibold text-slate-700">Alors</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-[11px] text-slate-600">Type budget</label>
              <select
                className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs"
                value={ruleBudgetType}
                onChange={(event) => {
                  const nextBudgetType = event.target.value === "AEP" ? "AEP" : "ASC";
                  setRuleBudgetType(nextBudgetType);
                  setRuleOperationTypeId("");
                }}
              >
                <option value="ASC">ASC</option>
                <option value="AEP">AEP</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[11px] text-slate-600">Catégorie</label>
              <select
                className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs"
                value={ruleOperationTypeId}
                onChange={(event) => setRuleOperationTypeId(event.target.value)}
              >
                <option value="">Choisir...</option>
                {(ruleBudgetType === "AEP" ? aepOperationTypes : ascOperationTypes).map((operationType) => (
                  <option key={operationType.id} value={operationType.id}>
                    {operationType.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-1.5">
          <Button type="button" variant="outline" className="h-8 text-xs" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button type="button" variant="success" className="h-8 text-xs" onClick={onCreateRule} disabled={ruleActionInProgress === "create"}>
            {ruleActionInProgress === "create"
              ? (
                <span className="inline-flex items-center gap-1.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Création...
                </span>
              )
              : "Créer la règle"}
          </Button>
        </div>

        <div className="rounded-md border bg-white overflow-hidden">
          <div className="px-2 py-1 text-[11px] font-semibold text-slate-700 border-b bg-slate-50">
            Règles de catégorisation (
            {sortedLocalRules.length}
            )
          </div>
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-2 py-1 text-[10px] uppercase tracking-wide text-slate-600">Nom</th>
                <th className="text-left px-2 py-1 text-[10px] uppercase tracking-wide text-slate-600">Condition</th>
                <th className="text-left px-2 py-1 text-[10px] uppercase tracking-wide text-slate-600">Catégorie</th>
                <th className="text-right px-2 py-1 text-[10px] uppercase tracking-wide text-slate-600 w-[140px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isCategorizationRulesLoading && (
                <tr>
                  <td className="px-2 py-1.5 text-slate-500" colSpan={4}>Chargement...</td>
                </tr>
              )}
              {!isCategorizationRulesLoading && sortedLocalRules.length === 0 && (
                <tr>
                  <td className="px-2 py-1.5 text-slate-500" colSpan={4}>Aucune règle pour le moment.</td>
                </tr>
              )}
              {!isCategorizationRulesLoading && sortedLocalRules.map((rule) => (
                <tr key={rule.id} className="border-b border-slate-100 last:border-b-0">
                  <td className="px-2 py-1.5 text-slate-800">{rule.name}</td>
                  <td className="px-2 py-1.5 text-slate-600 truncate">
                    {rule.conditions.map((condition) => {
                      if (condition.kind === "bank_account") {
                        const accountName = bankAccounts.find((account) => account.id === condition.bankAccountId)?.name
                          || condition.bankAccountId;
                        return `Compte bancaire = "${accountName}"`;
                      }
                      if (condition.kind === "amount") {
                        const operatorLabel = RULE_AMOUNT_OPERATOR_OPTIONS.find((option) => option.value === condition.amountOperator)?.label ?? condition.amountOperator;
                        const suffix = condition.absoluteValue ? " (valeur absolue)" : "";
                        if (condition.amountOperator === "between") {
                          return `Montant ${operatorLabel.replace("...", `${condition.amountValue || "0,00"} et ${condition.amountValueTo || "0,00"}`)}${suffix}`;
                        }
                        return `Montant ${operatorLabel.replace("...", condition.amountValue || "0,00")}${suffix}`;
                      }
                      const operatorLabel = RULE_TEXT_OPERATOR_OPTIONS.find((option) => option.value === condition.textOperator)?.label ?? condition.textOperator;
                      return `${operatorLabel} "${condition.textValue}"`;
                    }).join(" ET ")}
                  </td>
                  <td className="px-2 py-1.5 text-slate-700 truncate">
                    {rule.budgetType}
                    {" "}
                    ·
                    {" "}
                    {rule.operationTypeName}
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex justify-end gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-6 text-[10px] px-2"
                        onClick={() => onToggleRuleActive(rule.id)}
                        disabled={ruleActionInProgress === `delete-${rule.id}` || ruleActionInProgress === "create"}
                      >
                        {rule.isActive ? "Désactiver" : "Activer"}
                      </Button>
                      <Button
                        type="button"
                        variant="destructiveGhost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => onDeleteRule(rule.id)}
                        disabled={ruleActionInProgress === `toggle-${rule.id}` || ruleActionInProgress === "create"}
                        aria-label="Supprimer la règle"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </BaseDrawer>
  );
};

export default RulesManagerDrawer;
